import React, { useState, useContext, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Checkbox,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  DataGridPremium,
  GridToolbarContainer,
  GridToolbarExport,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
} from "@mui/x-data-grid-premium";
import { LicenseInfo } from "@mui/x-license";
import KeycloakContext from "KeycloakProvider";
import {
  useGetOutageTrackerQuery,
  useUpdateOutageTrackerMutation,
} from "api/homeApi";
import { reportError } from "utils/reportError";
import ContentUnavailable from "components/ContentUnavailable";

// License for DataGridPremium
LicenseInfo.setLicenseKey(
  "7b2a7be60c5eff8e15f1f2ae43e1260dTz0xMjMyNTksRT0xNzk3NDY1NTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y"
);

// Reason options
const REASON_OPTIONS = [
  "CellHub Replacement",
  "Radio Failure",
  "Bundle Upgrade",
  "eNB Config Change",
  "MOCN Config Change",
  "Radio Manager Alarm",
  "Server Replacement",
  "SU Venue Config",
  "Performance Team",
  "SPV Alarm",
  "CBRS Alarm",
  "MOCN Alarm",
  "Network Equipment Alarm",
  "CPRI Replacement",
  "Power Outage",
  "ISP Outage",
  "JMA Testing",
  "Construction",
  "Other",
];

// Owner options
const OWNER_OPTIONS = ["JMA", "SU", "PLN"];

const MONTH_LOOKBACK = 24;

const getMonthOptions = (count = MONTH_LOOKBACK) => {
  const now = new Date();
  const options = [];

  for (let i = 1; i <= count; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const value = `${year}-${String(month).padStart(2, "0")}`;
    const label = date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    options.push({ value, month, year, label });
  }

  return options;
};

// Styled DataGrid
const StyledDataGridPremium = styled(DataGridPremium)(({ theme }) => ({
  "& .MuiDataGrid-iconButtonContainer": {
    visibility: "visible",
    width: "auto",
  },
  "& .MuiDataGrid-sortIcon": {
    opacity: "inherit !important",
  },
  "& .MuiDataGrid-cell": {
    paddingTop: "5px !important",
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#1565C0",
    color: "#fff !important",
  },
  "& .MuiDataGrid-sortIcon, .MuiDataGrid-filterIcon": {
    color: "#1565C0 !important",
  },
  "& .MuiDataGrid-columnHeader:hover": {
    color: "#fff !important",
  },
  "& .MuiDataGrid-headerFilterRow .MuiInputBase-root": {
    backgroundColor: "white !important",
  },
  "& .MuiDataGrid-columnHeader.MuiDataGrid-columnHeader--filter.MuiDataGrid-withBorderColor":
    {
      backgroundColor: "white !important",
    },
  "& .MuiDataGrid-filterIcon": {
    color: "#1565C0 !important",
  },
  "& .MuiDataGrid-toolbarContainer .MuiButtonBase-root": {
    color: "#005AA6",
  },
  "& .MuiDataGrid-menuIconButton": {
    color: "#fff",
  },
  "& .to-update-row": {
    backgroundColor: "#FFF3E0 !important",
    "&:hover": {
      backgroundColor: "#FFE0B2 !important",
    },
  },
}));

const PageContainer = styled(Box)({
  backgroundColor: "#F5F5F5",
  minHeight: "100vh",
  padding: "2rem",
});

const ContentCard = styled(Box)({
  backgroundColor: "#FFFFFF",
  borderRadius: "12px",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  padding: "1.5rem",
});

const PageTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "1.75rem",
  color: "#1565C0",
  textAlign: "center",
  marginBottom: "1.5rem",
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)({
  marginBottom: "1rem",
  backgroundColor: "#F5F5F5",
  borderRadius: "12px",
  padding: "4px",
  "& .MuiToggleButton-root": {
    border: "none",
    borderRadius: "8px !important",
    padding: "8px 16px",
    textTransform: "none",
    fontFamily: "Montserrat",
    fontWeight: 500,
    color: "#666",
    "&.Mui-selected": {
      backgroundColor: "#FFFFFF",
      color: "#1565C0",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        backgroundColor: "#FFFFFF",
      },
    },
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.04)",
    },
  },
});

// Helper to format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${month}-${day}-${year} | ${hours}:${minutes}`;
};

const rowMatchesMonth = (row, selectedMonth) => {
  if (!selectedMonth) return true;
  if (!row?.start_time) return false;

  const datePart = String(row.start_time).split(" ")[0] || "";
  const [yearStr, monthStr] = datePart.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);

  if (!Number.isInteger(year) || !Number.isInteger(month)) return false;

  return year === selectedMonth.year && month === selectedMonth.month;
};

const OutageTracker = () => {
  const { keycloak } = useContext(KeycloakContext);
  const username = keycloak?.tokenParsed?.preferred_username || "unknown";

  const [filterMode, setFilterMode] = useState("all");
  const [selectedMonthKey, setSelectedMonthKey] = useState("current");
  const [pendingUpdates, setPendingUpdates] = useState({}); // { rowId: { reason, owner } }
  const [selectedRows, setSelectedRows] = useState(new Set()); // Track selected row IDs
  const [massEditReason, setMassEditReason] = useState(""); // Mass edit reason
  const [massEditOwner, setMassEditOwner] = useState(""); // Mass edit owner

  const monthOptions = useMemo(() => getMonthOptions(), []);
  const selectedMonthConfig = useMemo(
    () => monthOptions.find((option) => option.value === selectedMonthKey),
    [monthOptions, selectedMonthKey]
  );

  const {
    data: responseData,
    isLoading,
    isError,
    refetch,
  } = useGetOutageTrackerQuery(
    selectedMonthConfig
      ? { year: selectedMonthConfig.year, month: selectedMonthConfig.month }
      : undefined
  );
  const [updateOutage] = useUpdateOutageTrackerMutation();
  const errorReported = useRef(false);

  useEffect(() => {
    // Avoid cross-month bulk edits by resetting selections when month changes.
    setSelectedRows(new Set());
    setPendingUpdates({});
    setMassEditReason("");
    setMassEditOwner("");
  }, [selectedMonthKey]);

  useEffect(() => {
    if (
      !isLoading &&
      !isError &&
      responseData &&
      Array.isArray(responseData.data) &&
      responseData.data.length === 0 &&
      !errorReported.current
    ) {
      errorReported.current = true;
      reportError({
        page: "Outage Tracker",
        detail: "data array returned empty from /getOutageTracker/",
      });
    }
  }, [isLoading, isError, responseData]);

  const apiRef = useGridApiRef();

  // Build rows from API data
  const rows = useMemo(() => {
    if (!responseData?.data) return [];
    return responseData.data.map((item) => ({
      id: item.id,
      hostname: item.hostname,
      cellhub_serial: item.cellhub_serial,
      start_time: item.start_time,
      stop_time: item.stop_time,
      reason: item.reason || "",
      owner: item.owner || "",
      needsUpdate: !item.reason || !item.owner,
    }));
  }, [responseData]);

  // Filter rows based on mode
  const filteredRows = useMemo(() => {
    const monthScopedRows = rows.filter((row) =>
      rowMatchesMonth(row, selectedMonthConfig)
    );

    if (filterMode === "all") return monthScopedRows;
    return monthScopedRows.filter((row) => row.needsUpdate);
  }, [rows, filterMode, selectedMonthConfig]);

  // Handle dropdown changes
  const handleReasonChange = async (rowId, value) => {
    const currentPending = pendingUpdates[rowId] || {};
    const row = rows.find((r) => r.id === rowId);
    const newPending = {
      reason: value,
      owner: currentPending.owner || row?.owner || "",
    };

    setPendingUpdates((prev) => ({ ...prev, [rowId]: newPending }));

    // If both are filled, send update
    if (newPending.reason && newPending.owner) {
      try {
        await updateOutage({
          id: rowId,
          reason: newPending.reason,
          owner: newPending.owner,
          modified_by: username,
        }).unwrap();
        refetch();
        // Clear pending
        setPendingUpdates((prev) => {
          const updated = { ...prev };
          delete updated[rowId];
          return updated;
        });
      } catch (error) {
        console.error("Failed to update outage:", error);
      }
    }
  };

  const handleOwnerChange = async (rowId, value) => {
    const currentPending = pendingUpdates[rowId] || {};
    const row = rows.find((r) => r.id === rowId);
    const newPending = {
      reason: currentPending.reason || row?.reason || "",
      owner: value,
    };

    setPendingUpdates((prev) => ({ ...prev, [rowId]: newPending }));

    // If both are filled, send update
    if (newPending.reason && newPending.owner) {
      try {
        await updateOutage({
          id: rowId,
          reason: newPending.reason,
          owner: newPending.owner,
          modified_by: username,
        }).unwrap();
        refetch();
        // Clear pending
        setPendingUpdates((prev) => {
          const updated = { ...prev };
          delete updated[rowId];
          return updated;
        });
      } catch (error) {
        console.error("Failed to update outage:", error);
      }
    }
  };

  // Handle row checkbox change
  const handleRowCheckboxChange = (rowId, isChecked) => {
    setSelectedRows((prev) => {
      const updated = new Set(prev);
      if (isChecked) {
        updated.add(rowId);
      } else {
        updated.delete(rowId);
      }
      return updated;
    });
  };

  const getVisibleFilteredRowIds = () => {
    try {
      if (apiRef.current) {
        return gridFilteredSortedRowIdsSelector(apiRef);
      }
    } catch (error) {
      // During first render the grid state may not be ready yet.
    }

    return filteredRows.map((row) => row.id);
  };

  // Handle select all checkbox
  const handleSelectAllChange = (isChecked) => {
    const visibleRowIds = getVisibleFilteredRowIds();

    setSelectedRows((prev) => {
      const updated = new Set(prev);

      if (isChecked) {
        visibleRowIds.forEach((rowId) => updated.add(rowId));
      } else {
        visibleRowIds.forEach((rowId) => updated.delete(rowId));
      }

      return updated;
    });
  };

  const visibleRowIds = getVisibleFilteredRowIds();

  const allVisibleRowsSelected =
    visibleRowIds.length > 0 && visibleRowIds.every((rowId) => selectedRows.has(rowId));

  const someVisibleRowsSelected =
    visibleRowIds.some((rowId) => selectedRows.has(rowId)) && !allVisibleRowsSelected;

  // Handle mass edit save
  const handleMassEditSave = async () => {
    if (selectedRows.size === 0 || !massEditReason || !massEditOwner) {
      alert("Please select rows and both reason and owner");
      return;
    }

    try {
      const updatePromises = Array.from(selectedRows).map((rowId) =>
        updateOutage({
          id: rowId,
          reason: massEditReason,
          owner: massEditOwner,
          modified_by: username,
        }).unwrap()
      );

      await Promise.all(updatePromises);
      refetch();
      // Clear selection and mass edit fields
      setSelectedRows(new Set());
      setMassEditReason("");
      setMassEditOwner("");
    } catch (error) {
      console.error("Failed to save mass edit:", error);
      alert("Failed to save changes. Please try again.");
    }
  };

  // Column definitions
  const columns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          checked={allVisibleRowsSelected}
          indeterminate={someVisibleRowsSelected}
          onChange={(e) => handleSelectAllChange(e.target.checked)}
          sx={{ padding: "4px" }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedRows.has(params.row.id)}
          onChange={(e) => handleRowCheckboxChange(params.row.id, e.target.checked)}
          sx={{ padding: "4px" }}
        />
      ),
    },
    {
      field: "hostname",
      headerName: "Hostname",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "cellhub_serial",
      headerName: "Cellhub Serial Number",
      flex: 1.5,
      minWidth: 250,
    },
    {
      field: "start_time",
      headerName: "Start Time",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.875rem" }}>
          {formatDateTime(params.value)}
        </Typography>
      ),
    },
    {
      field: "stop_time",
      headerName: "Stop Time",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: "Montserrat", fontSize: "0.875rem" }}>
          {formatDateTime(params.value)}
        </Typography>
      ),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const pendingValue = pendingUpdates[params.row.id]?.reason;
        const currentValue = pendingValue || params.value;

        return (
          <Select
            value={currentValue || ""}
            onChange={(e) => handleReasonChange(params.row.id, e.target.value)}
            size="small"
            displayEmpty
            fullWidth
            sx={{
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              "& .MuiSelect-select": {
                padding: "6px 12px",
              },
            }}
          >
            <MenuItem value="" disabled>
              <em>-- Select a reason --</em>
            </MenuItem>
            {REASON_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "owner",
      headerName: "Owner",
      width: 150,
      renderCell: (params) => {
        const pendingValue = pendingUpdates[params.row.id]?.owner;
        const currentValue = pendingValue || params.value;

        return (
          <Select
            value={currentValue || ""}
            onChange={(e) => handleOwnerChange(params.row.id, e.target.value)}
            size="small"
            displayEmpty
            fullWidth
            sx={{
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              "& .MuiSelect-select": {
                padding: "6px 12px",
              },
            }}
          >
            <MenuItem value="" disabled>
              <em>-- Select an owner --</em>
            </MenuItem>
            {OWNER_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
  ];

  // Row class for highlighting rows that need update
  const getRowClassName = (params) => {
    if (params.row.needsUpdate) {
      return "to-update-row";
    }
    return "";
  };

  // Custom toolbar
  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ padding: "0.8rem" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            color: "#0a2240",
            letterSpacing: "-0.5px",
            fontFamily: "Montserrat",
            mr: 2,
          }}
        >
          SU Outage Tracker
        </Typography>
        <div style={{ flexGrow: 1 }} />
        <GridToolbarExport
          csvOptions={{ disableToolbarButton: true }}
          printOptions={{ disableToolbarButton: true }}
          excelOptions={{
            fileName: "outage-tracker-export",
            allColumns: true,
          }}
          slotProps={{
            tooltip: { title: "Download Excel" },
            button: { variant: "outlined", style: { marginRight: "0.5rem" } },
          }}
        />
      </GridToolbarContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <ContentCard sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </ContentCard>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <ContentCard>
          <Alert severity="error">
            Failed to load outage data. Please try again later.
          </Alert>
        </ContentCard>
      </PageContainer>
    );
  }

  if (!isLoading && responseData?.data?.length === 0) {
    return (
      <PageContainer>
        <ContentCard>
          <ContentUnavailable page="Outage Tracker" />
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentCard>
        <PageTitle>SU Outage Tracker</PageTitle>

        {/* Filter Toggle */}
        <StyledToggleButtonGroup
          value={filterMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode !== null) setFilterMode(newMode);
          }}
        >
          <ToggleButton value="all">Show all</ToggleButton>
          <ToggleButton value="to-update">To be updated</ToggleButton>
        </StyledToggleButtonGroup>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginBottom: "1rem",
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 600,
              color: "#0a2240",
              minWidth: "fit-content",
            }}
          >
            Month:
          </Typography>
          <Select
            value={selectedMonthKey}
            onChange={(e) => setSelectedMonthKey(e.target.value)}
            size="small"
            sx={{
              minWidth: 220,
              fontFamily: "Montserrat",
              backgroundColor: "#fff",
            }}
          >
            <MenuItem value="current">Current (default)</MenuItem>
            {monthOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Date Range Info */}
        {responseData?.date_range && (
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "0.875rem",
              color: "#666",
              marginBottom: "1rem",
            }}
          >
            Date Range: {formatDateTime(responseData.date_range.start)} —{" "}
            {formatDateTime(responseData.date_range.stop)}
          </Typography>
        )}

        {/* Mass Edit Toolbar */}
        {selectedRows.size > 0 && (
          <Box
            sx={{
              backgroundColor: "#E3F2FD",
              border: "1px solid #1565C0",
              borderRadius: "8px",
              padding: "1rem",
              marginBottom: "1rem",
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 500,
                color: "#1565C0",
              }}
            >
              {selectedRows.size} row{selectedRows.size > 1 ? "s" : ""} selected
            </Typography>

            <Select
              value={massEditReason}
              onChange={(e) => setMassEditReason(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                fontFamily: "Montserrat",
                minWidth: 200,
                backgroundColor: "white",
              }}
            >
              <MenuItem value="" disabled>
                <em>Select reason</em>
              </MenuItem>
              {REASON_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={massEditOwner}
              onChange={(e) => setMassEditOwner(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                fontFamily: "Montserrat",
                minWidth: 150,
                backgroundColor: "white",
              }}
            >
              <MenuItem value="" disabled>
                <em>Select owner</em>
              </MenuItem>
              {OWNER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>

            <Button
              variant="contained"
              sx={{
                backgroundColor: "#1565C0",
                fontFamily: "Montserrat",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#0D47A1",
                },
              }}
              onClick={handleMassEditSave}
              disabled={!massEditReason || !massEditOwner}
            >
              Save Changes
            </Button>

            <Button
              variant="outlined"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 600,
                textTransform: "none",
                borderColor: "#1565C0",
                color: "#1565C0",
                "&:hover": {
                  borderColor: "#0D47A1",
                  color: "#0D47A1",
                },
              }}
              onClick={() => setSelectedRows(new Set())}
            >
              Clear Selection
            </Button>
          </Box>
        )}

        {/* Data Grid */}
        <Box sx={{ height: "70vh", width: "100%" }}>
          <StyledDataGridPremium
            rows={filteredRows}
            columns={columns}
            apiRef={apiRef}
            getRowId={(row) => row.id}
            getRowClassName={getRowClassName}
            headerFilters
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              headerFilterCell: {
                sx: {
                  backgroundColor: "white",
                },
              },
            }}
            sx={{
              border: "none",
              borderRadius: 1,
              backgroundColor: "#fff",
              "& .MuiDataGrid-cell": {
                fontFamily: "Montserrat",
              },
            }}
          />
        </Box>
      </ContentCard>
    </PageContainer>
  );
};

export default OutageTracker;
